// src/components/admin/tabs/LiveTasksTab.jsx
// 实时任务：分镜 / 自动流 / Agent / 直接生成四张表，5 秒轮询（可关闭），支持停止自动流任务。
// 从 AdminDashboard.jsx 抽取；API 调用、轮询、window.confirm 流程逐字保留。
import { appConfirm } from '../../../services/appDialogs';
import React, { useState, useCallback } from "react";
import { RefreshCw, XCircle } from "lucide-react";
import { fetchLiveTasks, cancelAiTask } from "../../../services/adminService";
import { useSequentialPolling } from "../../../hooks/useSequentialPolling";
import {
  Panel, ErrorNotice, TaskTable, StatusBadge, DangerButton, AdminScopeFilterBar,
} from "../adminUi.jsx";
import { useAdminScope, rowMatchesAdminScope } from "../adminUtils";

const ACCENT = "var(--color-warning)";

// 任务状态语义色（本 tab 专用；未知状态由 StatusBadge 回退为 muted）
const TASK_STATUS_COLORS = {
  success: 'var(--color-success)',
  failed: 'var(--color-error)',
  processing: 'var(--color-warning)',
  pending: 'var(--color-info)',
};

const formatElapsed = (ms) => {
  if (ms == null) return '-';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m${s % 60}s`;
};

const ProgressCell = ({ value }) => {
  const n = Number(value);
  const safe = Number.isFinite(n) ? Math.max(0, Math.min(100, n)) : null;
  if (safe == null) return '-';
  return (
    <div className="min-w-[90px]">
      <div className="flex justify-between text-2xs font-mono-num mb-1" style={{ color: 'var(--color-text-muted)' }}>
        <span>{safe}%</span>
      </div>
      <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}>
        <div className="h-full rounded-full" style={{ width: `${safe}%`, backgroundColor: ACCENT }} />
      </div>
    </div>
  );
};

const LiveTasksTab = ({ projectId, projectOptions }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [actionError, setActionError] = useState('');
  const [stoppingTaskId, setStoppingTaskId] = useState('');
  const [scope, setScope] = useAdminScope(projectId);

  const load = useCallback(async ({ signal } = {}) => {
    setLoading(true);
    try {
      const res = await fetchLiveTasks({
        projectId: scope.projectId,
        userId: scope.userId,
      }, { signal });
      if (signal?.aborted) return;
      setData(res);
    } catch (e) {
      if (!signal?.aborted) console.error(e);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [scope.projectId, scope.userId]);

  const refreshTasks = useSequentialPolling(load, 5000, { enabled: autoRefresh });

  const workflowRows = (data?.workflowTasks || []).filter(row => rowMatchesAdminScope(row, scope));

  const stopWorkflow = async (row) => {
    const id = row?.internalTaskId || row?.taskId;
    if (!id) return;
    if (!(await appConfirm(`确定停止 ${row.workflowName || row.taskType || '自动流任务'}？`))) return;
    setActionError('');
    setStoppingTaskId(id);
    try {
      await cancelAiTask(id);
      await refreshTasks();
    } catch (e) {
      setActionError(e?.message || '停止任务失败');
    } finally {
      setStoppingTaskId('');
    }
  };

  const firstLoading = loading && !data;

  const tableSkeleton = (
    <div className="space-y-2">
      <div className="skeleton h-3 w-full" />
      <div className="skeleton h-3 w-11/12" />
      <div className="skeleton h-3 w-4/5" />
    </div>
  );

  const statusColumn = { key: 'status', label: '状态', render: (v) => <StatusBadge label={v || '-'} color={TASK_STATUS_COLORS[v]} /> };

  return (
    <div>
      <AdminScopeFilterBar
        scope={scope}
        setScope={setScope}
        projectOptions={projectOptions}
        onRefresh={refreshTasks}
        loading={loading}
      />
      <div className="flex items-center gap-3 mb-4">
        <label className="flex items-center gap-2 text-xs cursor-pointer" style={{ color: 'var(--color-text-secondary)' }}>
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(e) => {
              const nextEnabled = e.target.checked;
              setAutoRefresh(nextEnabled);
              if (!nextEnabled) setLoading(false);
            }}
          />
          每 <span className="font-mono-num">5</span> 秒自动刷新
        </label>
        <button
          type="button"
          onClick={refreshTasks}
          title="刷新"
          aria-label="刷新"
          className="w-8 h-8 rounded-md border flex items-center justify-center cursor-pointer transition-colors duration-150"
          style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-secondary)", backgroundColor: "var(--color-bg-secondary)" }}
        >
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
        </button>
        <div className="ml-auto text-2xs font-mono-num" style={{ color: 'var(--color-text-muted)' }}>
          分镜: {data?.totalShotPolling ?? 0} · 自动流: {data?.totalWorkflowActive ?? 0} · Agent: {data?.totalAgentActive ?? 0} · 直接: {data?.totalOtherActive ?? 0}
        </div>
      </div>
      {actionError && <ErrorNotice message={actionError} />}

      <Panel title="分镜生成中" className="mb-4">
        {firstLoading ? tableSkeleton : (
          <TaskTable
            rows={(data?.shotTasks || []).filter(row => rowMatchesAdminScope(row, scope))}
            columns={[
              { key: 'userId', label: '用户' },
              { key: 'projectId', label: '项目', className: 'font-mono-num' },
              { key: 'shotNumber', label: '镜号', className: 'font-mono-num' },
              { key: 'kind', label: '类型' },
              { key: 'provider', label: 'Provider' },
              statusColumn,
              { key: 'taskId', label: 'TaskID', className: 'font-mono-num text-2xs' },
            ]}
          />
        )}
      </Panel>

      <Panel title="自动流任务" sub="一键解说剧 / 对话短剧自动流" className="mb-4">
        {firstLoading ? tableSkeleton : (
          <TaskTable
            rows={workflowRows}
            columns={[
              { key: 'userId', label: '用户' },
              { key: 'workflowName', label: '自动流' },
              { key: 'projectName', label: '项目', render: (v, row) => v || row.projectId || '-' },
              { key: 'currentEpisodeSortOrder', label: '当前集', render: (v, row) => v ? `第 ${v} 集` : (row.currentEpisodeId ? `ID ${row.currentEpisodeId}` : '-') },
              { key: 'stageText', label: '阶段', className: 'min-w-[180px]' },
              { key: 'progress', label: '进度', render: (v) => <ProgressCell value={v} /> },
              { key: 'elapsedMs', label: '耗时', className: 'font-mono-num', render: (v) => formatElapsed(v) },
              { key: 'taskId', label: 'TaskID', className: 'font-mono-num text-2xs' },
              {
                key: 'actions',
                label: '操作',
                render: (_, row) => row.canStop ? (
                  <DangerButton
                    disabled={stoppingTaskId === (row.internalTaskId || row.taskId)}
                    onClick={() => stopWorkflow(row)}
                    className="inline-flex items-center gap-1 !h-7 !px-2.5"
                  >
                    <XCircle size={12} />
                    {stoppingTaskId === (row.internalTaskId || row.taskId) ? '停止中' : '停止'}
                  </DangerButton>
                ) : <span style={{ color: 'var(--color-text-muted)' }}>-</span>
              },
            ]}
          />
        )}
      </Panel>

      <Panel title="Agent 任务" className="mb-4">
        {firstLoading ? tableSkeleton : (
          <TaskTable
            rows={(data?.agentTasks || []).filter(row => rowMatchesAdminScope(row, scope))}
            columns={[
              { key: 'userId', label: '用户' },
              { key: 'taskType', label: '类型' },
              { key: 'provider', label: 'Provider' },
              statusColumn,
              { key: 'stageText', label: '阶段' },
              { key: 'progress', label: '进度', className: 'font-mono-num', render: (v) => v != null ? `${v}%` : '-' },
              { key: 'elapsedMs', label: '耗时', className: 'font-mono-num', render: (v) => formatElapsed(v) },
              { key: 'taskId', label: 'TaskID', className: 'font-mono-num text-2xs' },
            ]}
          />
        )}
      </Panel>

      <Panel title="直接生成任务">
        {firstLoading ? tableSkeleton : (
          <TaskTable
            rows={(data?.otherTasks || []).filter(row => rowMatchesAdminScope(row, scope))}
            columns={[
              { key: 'userId', label: '用户' },
              { key: 'taskType', label: '类型' },
              statusColumn,
              { key: 'stageText', label: '阶段' },
              { key: 'elapsedMs', label: '距上次更新', className: 'font-mono-num', render: (v) => formatElapsed(v) },
            ]}
          />
        )}
      </Panel>
    </div>
  );
};

export default LiveTasksTab;
